import { useTranslate } from 'react-admin';
import { Box, Card, Divider, Typography } from '@mui/material';

import type { VaultDerivedParams } from 'src/pages/admin/services/vaultParamsService';

import { VAULT_PARAMS_NS as NS } from './constants';
import { fieldUnits, formatFixedDecimals, formatValue } from './helpers';

export const DerivedParamsCard = ({ derived }: { derived: VaultDerivedParams | null }) => {
  const translate = useTranslate();
  if (!derived) return null;

  const units = fieldUnits(translate);
  const rows: [string, string][] = [
    [`${NS}.derived.sizeMultiple`, formatValue('multiplier', derived.sizeMultiple, units)],
    // Shown at the engine's NAV precision, as the PRD writes it (initial NAV 1.000000).
    [
      `${NS}.derived.initialNav`,
      formatFixedDecimals(derived.initialNav ?? '', derived.navDecimals),
    ],
    [
      `${NS}.derived.navPrecision`,
      translate(`${NS}.derived.navPrecisionValue`, {
        internal: derived.navDecimals,
        display: derived.navDisplayDecimals,
      }),
    ],
    [
      `${NS}.derived.sharePrecision`,
      translate(`${NS}.derived.sharePrecisionValue`, { dp: derived.shareDecimals }),
    ],
  ];

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">{translate(`${NS}.derived.title`)}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {translate(`${NS}.derived.subtitle`)}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
              {translate(label)}
            </Typography>
            <Typography
              component="dd"
              variant="subtitle1"
              sx={{ m: 0, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
